import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Tabs,
  Tab,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Divider,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  BarChart as BarChartIcon,
  ShowChart as ShowChartIcon,
  PieChart as PieChartIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Build as BuildIcon,
  Home as HomeIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarIcon,
  Comment as CommentIcon,
  Approval as ApprovalIcon
} from "@mui/icons-material";
import { format } from "date-fns";
import { ExecutePostWithParams, GetDataFromApiWithParams } from "../../../../../services/api/ExecuteApiRequests"

// Helper function to safely parse number
const safeParseNumber = (value) => {
  if (!value && value !== 0) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleanValue = value.replace(/,/g, '').trim();
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// Format number with commas
const formatNumber = (number) => {
  if (!number && number !== 0) return "0";
  return new Intl.NumberFormat('en-US').format(number);
};

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`permit-tabpanel-${index}`}
      aria-labelledby={`permit-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Bar Chart Component
const BarChart = ({ title, data, dataKey, color, formatValue, xAxisKey = "name" }) => {
  const items = Object.keys(data);
  
  if (items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(data).map(d => d[dataKey] || 0), 0);
  
  if (maxValue === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
      </Box>
    );
  }
  
  const barWidth = Math.min(50, Math.max(30, 80 - items.length * 2));
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        gap: 2.5, 
        minHeight: 400, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        padding: 2
      }}>
        {items.map((item) => {
          const value = data[item][dataKey] || 0;
          const height = maxValue > 0 ? (value / maxValue) * 280 : 0;
          const percentage = maxValue > 0 ? ((value / maxValue) * 100).toFixed(1) : 0;
          
          return (
            <Tooltip 
              key={item} 
              title={`${item}: ${formatValue(value)} (${percentage}% of max)`} 
              arrow 
              placement="top"
            >
              <Box sx={{ 
                textAlign: 'center', 
                minWidth: 70,
                maxWidth: 120,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}>
                <Box sx={{ 
                  position: 'relative',
                  height: height,
                  width: `${barWidth}px`,
                  bgcolor: color,
                  borderRadius: '4px 4px 0 0',
                  margin: '0 auto',
                  transition: 'all 0.3s ease',
                  opacity: 0.85,
                  '&:hover': {
                    opacity: 1,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                  }
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      position: 'absolute', 
                      top: -22, 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: color,
                      whiteSpace: 'nowrap',
                      display: height > 35 ? 'none' : 'block'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                </Box>
                
                {height > 35 && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: 'white',
                      mt: -2.5,
                      mb: 0.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                )}
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1.5, 
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    maxWidth: '100px',
                    wordWrap: 'break-word',
                    lineHeight: 1.2,
                    color: '#333'
                  }}
                >
                  {item.length > 20 ? item.substring(0, 18) + '...' : item}
                </Typography>
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    color: '#888',
                    fontSize: '0.65rem',
                    mt: 0.25
                  }}
                >
                  ({percentage}%)
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      <Box sx={{ 
        width: '100%', 
        bgcolor: '#f8f9fa', 
        mt: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 2, 
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
          Total {title}: {formatValue(Object.values(data).reduce((sum, d) => sum + (d[dataKey] || 0), 0))}
        </Typography>
      </Box>
    </Box>
  );
};

// Pie Chart Component
const PieChart = ({ title, data, formatValue }) => {
  const items = Object.keys(data);
  const total = items.reduce((sum, key) => sum + (data[key] || 0), 0);
  const colors = ['#4caf50', '#ff9800', '#e91e63', '#2196f3', '#9c27b0', '#d32f2f', '#0288d1', '#7b1fa2', '#e65100', '#c2185b', '#00695c', '#f44336'];
  
  if (items.length === 0 || total === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for the selected filters</Typography>
      </Box>
    );
  }
  
  let startAngle = 0;
  const centerX = 120, centerY = 120, radius = 90;
  const svgSize = 280;
  
  const getPercentage = (value) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : 0;
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 2, textAlign: 'center' }}>
        {title} - Total: {formatValue(total)}
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', width: svgSize, height: svgSize }}>
          <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
            {items.map((key, index) => {
              const value = data[key] || 0;
              const percentage = total > 0 ? (value / total) * 100 : 0;
              const angle = (percentage / 100) * 360;
              const endAngle = startAngle + angle;
              
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              
              const x1 = centerX + radius * Math.cos(startRad);
              const y1 = centerY + radius * Math.sin(startRad);
              const x2 = centerX + radius * Math.cos(endRad);
              const y2 = centerY + radius * Math.sin(endRad);
              
              const largeArcFlag = angle > 180 ? 1 : 0;
              const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
              
              const slice = (
                <path 
                  key={key} 
                  d={pathData} 
                  fill={colors[index % colors.length]} 
                  stroke="white" 
                  strokeWidth="2"
                  style={{ cursor: 'pointer', transition: 'opacity 0.3s' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                />
              );
              startAngle = endAngle;
              return slice;
            })}
            
            <circle cx={centerX} cy={centerY} r="25" fill="white" />
            <text x={centerX} y={centerY - 5} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#333">Total</text>
            <text x={centerX} y={centerY + 12} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1976d2">{formatValue(total)}</text>
          </svg>
        </Box>
        
        <Box sx={{ minWidth: 200, maxHeight: 250, overflowY: 'auto' }}>
          {items.map((key, index) => {
            const value = data[key] || 0;
            const percentage = getPercentage(value);
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: colors[index % colors.length], borderRadius: 1 }} />
                <Typography variant="caption" sx={{ flex: 1 }}>
                  {key.length > 20 ? key.substring(0, 18) + '...' : key} ({percentage}%)
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ color: colors[index % colors.length] }}>
                  {formatValue(value)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      
      <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">Total Items: {items.length}</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>Total Value: {formatValue(total)}</Typography>
      </Box>
    </Box>
  );
};

// Yearly Trend Line Chart
const YearlyTrendLineChart = ({ title, yearlyData, dataKeys, colors, formatValue }) => {
  const years = Object.keys(yearlyData).sort((a, b) => a - b);
  
  if (years.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No yearly data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(yearlyData).flatMap(d => 
    dataKeys.map(key => d[key] || 0)
  ), 0);
  
  const chartHeight = 350;
  const chartWidth = Math.max(600, years.length * 100);
  const padding = { top: 30, right: 40, bottom: 60, left: 80 };
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      
      <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
        {[0, 25, 50, 75, 100].map((percent) => {
          const y = chartHeight - padding.bottom - (percent / 100) * (chartHeight - padding.top - padding.bottom);
          const value = (maxValue * percent) / 100;
          return (
            <g key={percent}>
              <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="4" />
              <text x={padding.left - 8} y={y} textAnchor="end" fontSize="10" fill="#666">{formatValue(value)}</text>
            </g>
          );
        })}
        
        {dataKeys.map((dataKey, idx) => (
          <polyline
            key={dataKey}
            points={years.map((year, index) => {
              const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
              const value = yearlyData[year]?.[dataKey] || 0;
              const y = chartHeight - padding.bottom - (value / maxValue) * (chartHeight - padding.top - padding.bottom);
              return `${x},${y}`;
            }).join(' ')}
            fill="none"
            stroke={colors[idx % colors.length]}
            strokeWidth="2.5"
          />
        ))}
        
        {years.map((year, index) => {
          const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
          
          return (
            <g key={year}>
              {dataKeys.map((dataKey, idx) => {
                const value = yearlyData[year]?.[dataKey] || 0;
                const y = chartHeight - padding.bottom - (value / maxValue) * (chartHeight - padding.top - padding.bottom);
                const color = colors[idx % colors.length];
                
                return (
                  <g key={dataKey}>
                    <circle cx={x} cy={y} r="5" fill={color} stroke="white" strokeWidth="2" style={{ cursor: 'pointer' }} />
                    {value > 0 && (
                      <text 
                        x={x} 
                        y={idx === 0 ? y - 10 : y + 15} 
                        textAnchor="middle" 
                        fontSize="8" 
                        fill={color}
                      >
                        {formatValue(value)}
                      </text>
                    )}
                  </g>
                );
              })}
              <text x={x} y={chartHeight - padding.bottom + 20} textAnchor="middle" fontSize="11" fontWeight="bold">{year}</text>
            </g>
          );
        })}
        
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
        <line x1={padding.left} y1={chartHeight - padding.bottom} x2={chartWidth - padding.right} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
        
        <text x={20} y={chartHeight / 2} textAnchor="middle" fontSize="11" fill="#666" transform={`rotate(-90, 20, ${chartHeight / 2})`}>Number of Permits</text>
        <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="11" fill="#666">Year</text>
      </svg>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2, flexWrap: 'wrap' }}>
        {dataKeys.map((dataKey, idx) => {
          let label = "";
          if (dataKey === "totalRequests") label = "Total Requests";
          else if (dataKey === "totalIssued") label = "Permits Issued";
          else if (dataKey === "totalPending") label = "Pending";
          else if (dataKey === "totalOccupancy") label = "Occupancy Permits";
          else label = dataKey;
          
          return (
            <Box key={dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 3, bgcolor: colors[idx % colors.length], borderRadius: 1 }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
            </Box>
          );
        })}
      </Box>
      
      <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">Years: {years.join(' → ')}</Typography>
      </Box>
    </Box>
  );
};

// Main Building Permit Dashboard Component
export default function BuildingPermitDashboard() {
  // State Management
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPark, setSelectedPark] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [startYear, setStartYear] = useState("all");
  const [endYear, setEndYear] = useState("all");
  const [startMonth, setStartMonth] = useState("all");
  const [endMonth, setEndMonth] = useState("all");
  
  // Chart States
  const [showChart, setShowChart] = useState(false);
  const [chartView, setChartView] = useState("park");
  const [parkChartTab, setParkChartTab] = useState(0);
  const [parkChartType, setParkChartType] = useState('bar');
  
  // Data States
  const [buildingPermits, setBuildingPermits] = useState([]);
  const [permitComments, setPermitComments] = useState([]);
  const [occupancyPermits, setOccupancyPermits] = useState([]);
  const [occupancyFollowUps, setOccupancyFollowUps] = useState([]);
  const [filteredPermits, setFilteredPermits] = useState([]);
  
  // Dynamic filter options
  const [parksList, setParksList] = useState(["all"]);
  const [statusesList, setStatusesList] = useState(["all"]);
  
  // Dialog States
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedPermit, setSelectedPermit] = useState(null);

  // Generate last 10 years dynamically
  const getLastNYears = (n) => {
    const currentYear = new Date().getFullYear();
    const years = ["all"];
    for (let i = 0; i < n; i++) {
      years.push((currentYear - i).toString());
    }
    return years;
  };
  
  const years = getLastNYears(10);
  
  const months = [
    { value: "all", label: "All Months" },
    { value: "01", label: "January" }, { value: "02", label: "February" },
    { value: "03", label: "March" }, { value: "04", label: "April" },
    { value: "05", label: "May" }, { value: "06", label: "June" },
    { value: "07", label: "July" }, { value: "08", label: "August" },
    { value: "09", label: "September" }, { value: "10", label: "October" },
    { value: "11", label: "November" }, { value: "12", label: "December" }
  ];

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch(statusLower) {
      case 'issued':
      case 'approved':
        return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'pending':
      case 'under_review':
        return { bg: '#fff3e0', color: '#ef6c00' };
      case 'rejected':
      case 'cancelled':
        return { bg: '#ffebee', color: '#c62828' };
      default: 
        return { bg: '#f5f5f5', color: '#616161' };
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      return format(new Date(date), 'dd/MM/yyyy');
    } catch {
      return "Invalid Date";
    }
  };

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [permitsRes, commentsRes, occupancyRes, followUpRes] = await Promise.all([
          GetDataFromApiWithParams(`design-management/building-permits1`),
          GetDataFromApiWithParams(`design-management/building-permit-comments`),
          GetDataFromApiWithParams(`design-management/occupancy-permits`),
          GetDataFromApiWithParams(`design-management/occupancy-permit-follow-up`)
        ]);
        
        let permits = [];
        let comments = [];
        let occupancy = [];
        let followUps = [];
        
        if (permitsRes && Array.isArray(permitsRes)) permits = permitsRes;
        else if (permitsRes?.data && Array.isArray(permitsRes.data)) permits = permitsRes.data;
        
        if (commentsRes && Array.isArray(commentsRes)) comments = commentsRes;
        else if (commentsRes?.data && Array.isArray(commentsRes.data)) comments = commentsRes.data;
        
        if (occupancyRes && Array.isArray(occupancyRes)) occupancy = occupancyRes;
        else if (occupancyRes?.data && Array.isArray(occupancyRes.data)) occupancy = occupancyRes.data;
        
        if (followUpRes && Array.isArray(followUpRes)) followUps = followUpRes;
        else if (followUpRes?.data && Array.isArray(followUpRes.data)) followUps = followUpRes.data;
        console.log("HEYYYYYYYYYYYY", permits)
        const formattedPermits = permits.map(permit => {
          let year = "2024";
          let month = "01";
          if (permit.requested_on) {
            const date = new Date(permit.requested_on);
            year = date.getFullYear().toString();
            month = (date.getMonth() + 1).toString().padStart(2, '0');
          }
          
          return {
            id: permit.id,
            permit_no: permit.permit_no || `BP-${permit.id}`,
            requested_on: permit.requested_on,
            requested_on_formatted: formatDate(permit.requested_on),
            request_type: permit.request_type || "New Construction",
            agreement_id: permit.agreement_id,
            expected_starting_date: formatDate(permit.expected_starting_date),
            expected_finishing_date: formatDate(permit.expected_finishing_date),
            no_of_residence_bldgs: permit.no_of_residence_bldgs || 0,
            no_of_commercial_bldgs: permit.no_of_commercial_bldgs || 0,
            no_of_sheds: permit.no_of_sheds || 0,
            no_of_other_bldgs: permit.no_of_other_bldgs || 0,
            total_buildings: (permit.no_of_residence_bldgs || 0) + (permit.no_of_commercial_bldgs || 0) + 
                            (permit.no_of_sheds || 0) + (permit.no_of_other_bldgs || 0),
            status: permit.status || "Pending",
            description: permit.description || "N/A",
            parcel_size: permit.parcel_size || 0,
            permit_registered_by: permit.permit_registered_by,
            request_registered_on: formatDate(permit.request_registered_on),
            permit_issued_by: permit.permit_issued_by,
            issued_on: formatDate(permit.issued_on),
            year: year,
            month: month,
            park_name: permit.park_name || "N/A"
          };
        });
        
        const formattedComments = comments.map(comment => ({
          id: comment.no,
          permit_no: comment.permit_no,
          date_of_comment: formatDate(comment.date_of_comment),
          commented_by: comment.commented_by,
          comment: comment.comment,
          is_approval: comment.is_approval === 1 || comment.is_approval === true,
          comment_type: comment.comment_type || "General"
        }));
        
        const formattedOccupancy = occupancy.map(occ => ({
          id: occ.id,
          building_permit_id: occ.building_permit_id,
          requested_on: formatDate(occ.requested_on),
          note: occ.note,
          status: occ.status || "Pending",
          approved_on: formatDate(occ.approved_on),
          approved_by: occ.approved_by,
          comment: occ.comment,
          description: occ.description
        }));
        
        const formattedFollowUps = followUps.map(fu => ({
          permit_id: fu.permit_id,
          followed_on: formatDate(fu.followed_on),
          checked_by: fu.checked_by,
          comment: fu.comment,
          percent_completed: fu.percent_completed || 0
        }));
        
        setBuildingPermits(formattedPermits);
        setFilteredPermits(formattedPermits);
        setPermitComments(formattedComments);
        setOccupancyPermits(formattedOccupancy);
        setOccupancyFollowUps(formattedFollowUps);
        
        const uniqueParks = new Set();
        formattedPermits.forEach(permit => {
          if (permit.park_name && permit.park_name !== "N/A") {
            uniqueParks.add(permit.park_name);
          }
        });
        setParksList(["all", ...Array.from(uniqueParks).sort()]);
        
        const uniqueStatuses = new Set();
        formattedPermits.forEach(permit => {
          if (permit.status) {
            uniqueStatuses.add(permit.status);
          }
        });
        setStatusesList(["all", ...Array.from(uniqueStatuses).sort()]);
        
      } catch (error) {
        console.error("Failed to fetch building permit data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Calculate summary statistics

  console.log("OLOLOLOLOL",filteredPermits)
  const summaryStats = useMemo(() => {
    const totalRequests = filteredPermits.length;
    const totalIssued = filteredPermits.filter(p => p.status === "Finished" || p.status === "Approved").length;
    const totalPending = filteredPermits.filter(p => p.status === "REQUESTED" || p.status === "Under Review").length;
    const totalRejected = filteredPermits.filter(p => p.status === "Rejected" || p.status === "Cancelled").length;
    const totalOccupancyRequests = occupancyPermits.length;
    const totalOccupancyApproved = occupancyPermits.filter(o => o.status === "Approved").length;
    const totalResidentialBuildings = filteredPermits.reduce((sum, p) => sum + (p.no_of_residence_bldgs || 0), 0);
    const totalCommercialBuildings = filteredPermits.reduce((sum, p) => sum + (p.no_of_commercial_bldgs || 0), 0);
    const averageCompletionRate = occupancyFollowUps.length > 0 ? 
      occupancyFollowUps.reduce((sum, f) => sum + (f.percent_completed || 0), 0) / occupancyFollowUps.length : 0;
    
    return {
      totalRequests,
      totalIssued,
      totalPending,
      totalRejected,
      totalOccupancyRequests,
      totalOccupancyApproved,
      totalResidentialBuildings,
      totalCommercialBuildings,
      averageCompletionRate
    };
  }, [filteredPermits, occupancyPermits, occupancyFollowUps]);

  // Chart data preparation
  const chartData = useMemo(() => {
    const statusDistribution = {
      "Issued": filteredPermits.filter(p => p.status === "Issued" || p.status === "Finished").length,
      "Pending": filteredPermits.filter(p => p.status === "REQUESTED" || p.status === "Pending").length,
      "Rejected": filteredPermits.filter(p => p.status === "Rejected" || p.status === "Cancelled").length
    };
    
    const buildingTypeDistribution = {
      "Residential": filteredPermits.reduce((sum, p) => sum + (p.no_of_residence_bldgs || 0), 0),
      "Commercial": filteredPermits.reduce((sum, p) => sum + (p.no_of_commercial_bldgs || 0), 0),
      "Sheds": filteredPermits.reduce((sum, p) => sum + (p.no_of_sheds || 0), 0),
      "Other": filteredPermits.reduce((sum, p) => sum + (p.no_of_other_bldgs || 0), 0)
    };
    
    const monthlyRequests = {
      "Jan": 0, "Feb": 0, "Mar": 0, "Apr": 0, "May": 0, "Jun": 0,
      "Jul": 0, "Aug": 0, "Sep": 0, "Oct": 0, "Nov": 0, "Dec": 0
    };
    
    const monthlyIssued = {
      "Jan": 0, "Feb": 0, "Mar": 0, "Apr": 0, "May": 0, "Jun": 0,
      "Jul": 0, "Aug": 0, "Sep": 0, "Oct": 0, "Nov": 0, "Dec": 0
    };
    
    const monthlyOccupancy = {
      "Jan": 0, "Feb": 0, "Mar": 0, "Apr": 0, "May": 0, "Jun": 0,
      "Jul": 0, "Aug": 0, "Sep": 0, "Oct": 0, "Nov": 0, "Dec": 0
    };
    
    filteredPermits.forEach(permit => {
      const monthNum = permit.month;
      const monthNames = { "01":"Jan","02":"Feb","03":"Mar","04":"Apr","05":"May","06":"Jun",
                          "07":"Jul","08":"Aug","09":"Sep","10":"Oct","11":"Nov","12":"Dec" };
      const monthName = monthNames[monthNum] || "Jan";
      monthlyRequests[monthName] = (monthlyRequests[monthName] || 0) + 1;
      
      if (permit.status === "Issued" || permit.status === "Approved") {
        monthlyIssued[monthName] = (monthlyIssued[monthName] || 0) + 1;
      }
    });
    
    occupancyPermits.forEach(occ => {
      if (occ.requested_on) {
        const date = new Date(occ.requested_on);
        const monthName = date.toLocaleString('default', { month: 'short' });
        monthlyOccupancy[monthName] = (monthlyOccupancy[monthName] || 0) + 1;
      }
    });
    
    const yearlyData = {};
    filteredPermits.forEach(permit => {
      const year = permit.year;
      if (!yearlyData[year]) {
        yearlyData[year] = { totalRequests: 0, totalIssued: 0, totalPending: 0, totalOccupancy: 0 };
      }
      yearlyData[year].totalRequests++;
      if (permit.status === "Issued" || permit.status === "Approved") {
        yearlyData[year].totalIssued++;
      }
      if (permit.status === "Pending" || permit.status === "Under Review") {
        yearlyData[year].totalPending++;
      }
    });
    
    occupancyPermits.forEach(occ => {
      if (occ.requested_on) {
        const year = new Date(occ.requested_on).getFullYear().toString();
        if (!yearlyData[year]) {
          yearlyData[year] = { totalRequests: 0, totalIssued: 0, totalPending: 0, totalOccupancy: 0 };
        }
        yearlyData[year].totalOccupancy++;
      }
    });
    
    const requestsByPark = {};
    filteredPermits.forEach(permit => {
      const park = permit.park_name || "Unassigned";
      requestsByPark[park] = (requestsByPark[park] || 0) + 1;
    });
    
    const commentsByType = {};
    permitComments.forEach(comment => {
      const type = comment.comment_type;
      commentsByType[type] = (commentsByType[type] || 0) + 1;
    });
    
    return { 
      statusDistribution, buildingTypeDistribution, monthlyRequests, monthlyIssued, 
      monthlyOccupancy, yearlyData, requestsByPark, commentsByType 
    };
  }, [filteredPermits, occupancyPermits, permitComments]);

  // Filter Data
  const applyFilters = useCallback(() => {
    let filteredList = [...buildingPermits];
    
    if (searchTerm) {
      filteredList = filteredList.filter(item =>
        item.permit_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.agreement_id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.park_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedPark !== "all") {
      filteredList = filteredList.filter(item => item.park_name === selectedPark);
    }
    
    if (selectedStatus !== "all") {
      filteredList = filteredList.filter(item => 
        item.status?.toLowerCase() === selectedStatus.toLowerCase()
      );
    }
    
    if (startYear !== "all" || endYear !== "all") {
      filteredList = filteredList.filter(item => {
        const itemYear = parseInt(item.year);
        const startYearNum = startYear !== "all" ? parseInt(startYear) : -Infinity;
        const endYearNum = endYear !== "all" ? parseInt(endYear) : Infinity;
        return itemYear >= startYearNum && itemYear <= endYearNum;
      });
    }
    
    if (startMonth !== "all" || endMonth !== "all") {
      filteredList = filteredList.filter(item => {
        const itemMonth = parseInt(item.month);
        const startMonthNum = startMonth !== "all" ? parseInt(startMonth) : 1;
        const endMonthNum = endMonth !== "all" ? parseInt(endMonth) : 12;
        return itemMonth >= startMonthNum && itemMonth <= endMonthNum;
      });
    }
    
    setFilteredPermits(filteredList);
    setPage(0);
  }, [buildingPermits, searchTerm, selectedPark, selectedStatus, startYear, endYear, startMonth, endMonth]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Permit No', 'Request Type', 'Request Date', 'Status', 'Residential', 'Commercial', 'Sheds', 'Other', 'Total Buildings', 'Park Name', 'Year', 'Month'];
    const data = filteredPermits.map(item => [
      item.permit_no,
      item.request_type,
      item.requested_on_formatted,
      item.status,
      item.no_of_residence_bldgs,
      item.no_of_commercial_bldgs,
      item.no_of_sheds,
      item.no_of_other_bldgs,
      item.total_buildings,
      item.park_name,
      item.year,
      item.month
    ]);
    
    const csvContent = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `building_permits_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedPark("all");
    setSelectedStatus("all");
    setStartYear("all");
    setEndYear("all");
    setStartMonth("all");
    setEndMonth("all");
  };

  const handleChangePage = (event, newPage) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
  const getCurrentPageData = () => { return filteredPermits.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage); };
  
  const handleViewDetails = (permit) => { 
    setSelectedPermit(permit); 
    setOpenDetailsDialog(true); 
  };
  
  const getCommentsForPermit = (permitNo) => {
    return permitComments.filter(c => c.permit_no === permitNo);
  };

  const renderParkChart = (title, data, dataKey, color, formatValue) => {
    const hasValidData = Object.values(data).some(item => {
      const value = item[dataKey];
      return value && value > 0;
    });
    
    if (!hasValidData) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
        </Box>
      );
    }
    
    if (parkChartType === 'pie') {
      const pieData = {};
      Object.entries(data).forEach(([key, val]) => { 
        const value = val[dataKey] || 0;
        if (value > 0) {
          pieData[key] = value;
        }
      });
      
      if (Object.keys(pieData).length === 0) {
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
          </Box>
        );
      }
      return <PieChart title={title} data={pieData} formatValue={formatValue} />;
    } else {
      return <BarChart title={title} data={data} dataKey={dataKey} color={color} formatValue={formatValue} />;
    }
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#f5f5f5', minHeight: '100vh', p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#1a237e', fontWeight: 'bold', textAlign: 'center', mb: 1 }}>
        Building Permit Management Dashboard
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 3 }}>
        Track and analyze building permit requests, approvals, and occupancy permits
      </Typography>
      
      {/* Summary Cards - Reduced Height */}
      <Grid container spacing={2} sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px', p : 2}}>
            <CardContent sx={{ textAlign: 'center', py: 0.25, px: 0.5, '&:last-child': { pb: 0.25 } }}>
              <AssignmentIcon sx={{ color: '#1976d2', fontSize: 14, mb: 0 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {formatNumber(summaryStats.totalRequests)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.6rem', lineHeight: 1.1 }}>Total Requests</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px', p:2}}>
            <CardContent sx={{ textAlign: 'center', py: 0.25, px: 0.5, '&:last-child': { pb: 0.25 } }}>
              <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 14, mb: 0 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {formatNumber(summaryStats.totalIssued)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.6rem', lineHeight: 1.1 }}>Permits Issued</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px', p:2 }}>
            <CardContent sx={{ textAlign: 'center', py: 0.25, px: 0.5, '&:last-child': { pb: 0.25 } }}>
              <PendingIcon sx={{ color: '#ed6c02', fontSize: 14, mb: 0 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {formatNumber(summaryStats.totalPending)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.6rem', lineHeight: 1.1 }}>Pending Requests</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px', p:2}}>
            <CardContent sx={{ textAlign: 'center', py: 0.25, px: 0.5, '&:last-child': { pb: 0.25 } }}>
              <HomeIcon sx={{ color: '#9c27b0', fontSize: 14, mb: 0 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {formatNumber(summaryStats.totalOccupancyRequests)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.6rem', lineHeight: 1.1 }}>Occupancy Requests</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        
      </Grid>
      
      {/* Filters Section */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by permit no, agreement ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                endAdornment: searchTerm && (<InputAdornment position="end"><ClearIcon sx={{ cursor: 'pointer', fontSize: 'small' }} onClick={() => setSearchTerm("")} /></InputAdornment>)
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={1.8}>
            <FormControl fullWidth size="small">
              <InputLabel>Park</InputLabel>
              <Select value={selectedPark} onChange={(e) => setSelectedPark(e.target.value)} label="Park">
                {parksList.map((park) => (<MenuItem key={park} value={park}>{park === "all" ? "All Parks" : park}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={1.8}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} label="Status">
                {statusesList.map((status) => (<MenuItem key={status} value={status}>{status === "all" ? "All Status" : status}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year From</InputLabel>
              <Select value={startYear} onChange={(e) => setStartYear(e.target.value)} label="Year From">
                {years.map((year) => (<MenuItem key={year} value={year}>{year === "all" ? "All Years" : year}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year To</InputLabel>
              <Select value={endYear} onChange={(e) => setEndYear(e.target.value)} label="Year To">
                {years.map((year) => (<MenuItem key={year} value={year}>{year === "all" ? "All Years" : year}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={resetFilters} startIcon={<ClearIcon />} size="small" sx={{ textTransform: 'none' }}>
              Reset
            </Button>
          </Grid>
        </Grid>
        
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Month From</InputLabel>
              <Select value={startMonth} onChange={(e) => setStartMonth(e.target.value)} label="Month From">
                {months.map((month) => (<MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Month To</InputLabel>
              <Select value={endMonth} onChange={(e) => setEndMonth(e.target.value)} label="Month To">
                {months.map((month) => (<MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={8} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="contained" color="primary" size="small" onClick={handleExportCSV} startIcon={<DownloadIcon />} sx={{ textTransform: 'none' }}>
              Export CSV
            </Button>
            <Button variant="outlined" size="small" onClick={() => setShowChart(!showChart)} sx={{ textTransform: 'none' }}>
              {showChart ? "Hide Chart" : "Show Chart"}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Conditional Rendering: Show Chart OR Table */}
      {showChart ? (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button variant={chartView === 'park' ? 'contained' : 'outlined'} size="small" onClick={() => setChartView('park')} sx={{ textTransform: 'none', minWidth: '120px' }}>
              <LocationOnIcon sx={{ mr: 1, fontSize: 18 }} />Park-wise View
            </Button>
            <Button variant={chartView === 'yearly' ? 'contained' : 'outlined'} size="small" onClick={() => setChartView('yearly')} sx={{ textTransform: 'none', minWidth: '120px' }}>
              <TimelineIcon sx={{ mr: 1, fontSize: 18 }} />Yearly Trend
            </Button>
          </Box>
          
          <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            {chartView === 'park' ? (
              <>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary">Chart Type:</Typography>
                    <ToggleButtonGroup value={parkChartType} exclusive onChange={(e, val) => val && setParkChartType(val)} size="small">
                      <ToggleButton value="bar">
                        <Tooltip title="Bar Chart">
                          <BarChartIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value="pie">
                        <Tooltip title="Pie Chart">
                          <PieChartIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={parkChartTab} onChange={(e, newValue) => setParkChartTab(newValue)}>
                    <Tab label="Permit Status Distribution" />
                    <Tab label="Building Type Distribution" />
                    <Tab label="Requests by Park" />
                    <Tab label="Comments by Type" />
                  </Tabs>
                </Box>
                
                <Box sx={{ mt: 2, minHeight: 450 }}>
                  <TabPanel value={parkChartTab} index={0}>
                    {parkChartType === 'pie' ? (
                      <PieChart title="Permit Status Distribution" data={chartData.statusDistribution} formatValue={(v) => formatNumber(v)} />
                    ) : (
                      <BarChart title="Permit Status Distribution" data={Object.fromEntries(Object.entries(chartData.statusDistribution).map(([k, v]) => [k, { value: v }]))} dataKey="value" color="#1976d2" formatValue={(v) => formatNumber(v)} />
                    )}
                  </TabPanel>
                  <TabPanel value={parkChartTab} index={1}>
                    {parkChartType === 'pie' ? (
                      <PieChart title="Building Type Distribution" data={chartData.buildingTypeDistribution} formatValue={(v) => formatNumber(v)} />
                    ) : (
                      <BarChart title="Building Type Distribution" data={Object.fromEntries(Object.entries(chartData.buildingTypeDistribution).map(([k, v]) => [k, { value: v }]))} dataKey="value" color="#4caf50" formatValue={(v) => formatNumber(v)} />
                    )}
                  </TabPanel>
                  <TabPanel value={parkChartTab} index={2}>
                    {renderParkChart("Permit Requests by Park", Object.fromEntries(Object.entries(chartData.requestsByPark).map(([k, v]) => [k, { value: v }])), "value", "#ff9800", (v) => formatNumber(v))}
                  </TabPanel>
                  <TabPanel value={parkChartTab} index={3}>
                    {parkChartType === 'pie' ? (
                      <PieChart title="Comments by Type" data={chartData.commentsByType} formatValue={(v) => formatNumber(v)} />
                    ) : (
                      <BarChart title="Comments by Type" data={Object.fromEntries(Object.entries(chartData.commentsByType).map(([k, v]) => [k, { value: v }]))} dataKey="value" color="#9c27b0" formatValue={(v) => formatNumber(v)} />
                    )}
                  </TabPanel>
                </Box>
              </>
            ) : (
              <YearlyTrendLineChart 
                title="Yearly Permit Trends" 
                yearlyData={chartData.yearlyData} 
                dataKeys={['totalRequests', 'totalIssued', 'totalPending', 'totalOccupancy']}
                colors={['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0']}
                formatValue={(v) => formatNumber(v)}
              />
            )}
          </Paper>
        </>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading building permit data...</Typography>
            </Box>
          ) : filteredPermits.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <Typography sx={{ color: 'text.secondary' }}>No building permit data available</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Permit No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Request Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Park Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Request Type</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Buildings</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Issued On</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getCurrentPageData().map((row) => {
                      const statusColors = getStatusColor(row.status);
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.permit_no}</TableCell>
                          <TableCell>{row.requested_on_formatted}</TableCell>
                          <TableCell>{row.park_name}</TableCell>
                          <TableCell>{row.request_type}</TableCell>
                          <TableCell align="center">{row.total_buildings}</TableCell>
                          <TableCell>
                            <Chip label={row.status} size="small" sx={{ bgcolor: statusColors.bg, color: statusColors.color, fontWeight: 'medium' }} />
                          </TableCell>
                          <TableCell>{row.issued_on || 'Not Issued'}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton size="small" color="primary" onClick={() => handleViewDetails(row)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination 
                rowsPerPageOptions={[5, 10, 25, 50]} 
                component="div" 
                count={filteredPermits.length} 
                rowsPerPage={rowsPerPage} 
                page={page} 
                onPageChange={handleChangePage} 
                onRowsPerPageChange={handleChangeRowsPerPage} 
              />
            </>
          )}
        </Paper>
      )}
      
      {/* Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
              Permit Details
            </Typography>
            <IconButton onClick={() => setOpenDetailsDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedPermit && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Permit No</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{selectedPermit.permit_no}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Request Date</Typography>
                  <Typography variant="body1">{selectedPermit.requested_on_formatted}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Park Name</Typography>
                  <Typography variant="body1">{selectedPermit.park_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Status</Typography>
                  <Chip label={selectedPermit.status} size="small" sx={getStatusColor(selectedPermit.status)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Agreement ID</Typography>
                  <Typography variant="body1">{selectedPermit.agreement_id || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Request Type</Typography>
                  <Typography variant="body1">{selectedPermit.request_type}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Expected Start Date</Typography>
                  <Typography variant="body1">{selectedPermit.expected_starting_date || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Expected Finish Date</Typography>
                  <Typography variant="body1">{selectedPermit.expected_finishing_date || 'N/A'}</Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 2 }}>Building Details</Typography>
                </Grid>
                
                <Grid item xs={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>Residential</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>{selectedPermit.no_of_residence_bldgs}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>Commercial</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>{selectedPermit.no_of_commercial_bldgs}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: '#fff3e0' }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>Sheds</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ed6c02' }}>{selectedPermit.no_of_sheds}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f3e5f5' }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>Other</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>{selectedPermit.no_of_other_bldgs}</Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5', mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Summary</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                      Total Buildings: {selectedPermit.total_buildings}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>Parcel Size: {formatNumber(selectedPermit.parcel_size)} sqm</Typography>
                  </Paper>
                </Grid>
                
                {selectedPermit.description && selectedPermit.description !== "N/A" && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: '#666' }}>Description</Typography>
                    <Typography variant="body2">{selectedPermit.description}</Typography>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 2 }}>Comments</Typography>
                  {getCommentsForPermit(selectedPermit.permit_no).map((comment, idx) => (
                    <Paper key={idx} sx={{ p: 1.5, mb: 1, bgcolor: comment.is_approval ? '#e8f5e9' : '#ffebee' }}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" fontWeight="bold">{comment.commented_by}</Typography>
                        <Typography variant="caption" color="textSecondary">{comment.date_of_comment}</Typography>
                      </Box>
                      <Typography variant="body2">{comment.comment}</Typography>
                      {comment.is_approval && <Chip label="Approval" size="small" color="success" sx={{ mt: 0.5 }} />}
                    </Paper>
                  ))}
                  {getCommentsForPermit(selectedPermit.permit_no).length === 0 && (
                    <Typography variant="body2" color="textSecondary">No comments available</Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setOpenDetailsDialog(false)} variant="outlined">Close</Button>
        </Box>
      </Dialog>
    </Box>
  );
}